import { useState, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import { useMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { HighlightsList } from '@hotjar/webapp/features/Highlights/List/HighlightsList';
import { useCurrentSite, useCurrentSiteId } from '@hotjar/site';
import { ALL_HIGHLIGHTS_ID } from '@hotjar/webapp/domains/collections/model';
import { usePageTitle } from '@hotjar/webapp/hooks/usePageTitle';
import { useNavigation } from '@hotjar/navigation';
import { useAccountExperiment } from '@hotjar/webapp/hooks/useExperiment';
import { useTrackingCodeState } from '@hotjar/webapp/hooks/useTrackingCode';
import { CollectionsSidebar } from './CollectionsSidebar';
import { useCollectionsList } from './use-collections-list';

const SIDEBAR_WIDTH = 230;

const SidebarWrapper = styled.div<{ topNavHeight: number }>`
  width: ${SIDEBAR_WIDTH}px;
  min-width: ${SIDEBAR_WIDTH}px;
  max-width: ${SIDEBAR_WIDTH}px;
  position: fixed;
  z-index: 1;
  height: calc(100vh + 2px - ${({ topNavHeight }) => topNavHeight}px);
  background-color: ${tokens.color.ui_background};
  overflow-x: hidden;
  overflow-y: auto;
`;

const ListWrapper = styled.div<{ noSidebar: boolean }>`
  margin-left: ${({ noSidebar }) => (noSidebar ? 'unset' : `${SIDEBAR_WIDTH}px`)};
  width: ${({ noSidebar }) => (noSidebar ? '100%' : `calc(100% - ${SIDEBAR_WIDTH}px)`)};
`;

const Container = styled.div`
  display: flex;
`;

export const CollectionsList = (): JSX.Element => {
  usePageTitle('Highlights - Hotjar');

  const { t } = useTranslation();

  const allHighlights = {
    highlightsCount: 0,
    id: ALL_HIGHLIGHTS_ID,
    name: t('collections:sidebar.all-highlights'),
  };
  const [searchQuery, setSearchQuery] = useState('');
  const urlParams = useMatch('/sites/:siteId/highlights/:collectionId');
  const { data: currentSite } = useCurrentSite();
  const currentSiteId = useCurrentSiteId();
  const { data: collections, status: collectionsStatus } = useCollectionsList(currentSiteId);
  const [collectionItems, setCollectionItems] = useState(collections ?? []);
  const defaultAllHighlights = collections?.[0] || allHighlights;
  const { topBarHeight: topNavHeight } = useNavigation();
  const { installed } = useTrackingCodeState();
  const { inVariant1: inEmptyStatesExperiment } = useAccountExperiment(
    'atv_empty_states_2023_03',
    currentSite?.account,
    !installed,
  );
  const isEmptyStateExperimentEnabled = !installed && inEmptyStatesExperiment;

  useEffect(() => {
    if (collections) {
      setCollectionItems(collections);
    } else {
      setCollectionItems([]);
    }
  }, [collections]);

  const selectedCollection = useMemo(() => {
    if (collections && urlParams?.params?.collectionId) {
      const selectedFromUrlCollection = collections.find(
        (collection) => collection.id === urlParams?.params?.collectionId,
      );

      return selectedFromUrlCollection ?? defaultAllHighlights;
    }

    return defaultAllHighlights;
  }, [urlParams?.params?.collectionId, collections, defaultAllHighlights]);

  const filteredCollections = useMemo(() => {
    if (!collectionItems) {
      return [];
    }

    return collectionItems
      .filter(
        (collection) =>
          collection.id !== ALL_HIGHLIGHTS_ID &&
          collection.name.toLowerCase().includes(searchQuery),
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [collectionItems, searchQuery]);

  return (
    <Container>
      {!isEmptyStateExperimentEnabled ? (
        <SidebarWrapper topNavHeight={topNavHeight}>
          <CollectionsSidebar
            collections={filteredCollections}
            currentSiteId={currentSiteId}
            defaultAllHighlights={defaultAllHighlights}
            selectedCollectionId={selectedCollection?.id}
            setSearchQuery={setSearchQuery}
          />
        </SidebarWrapper>
      ) : null}
      <ListWrapper noSidebar={isEmptyStateExperimentEnabled}>
        <HighlightsList
          isEmptyStateExperimentEnabled={isEmptyStateExperimentEnabled}
          collection={selectedCollection}
          collectionsStatus={collectionsStatus}
        />
      </ListWrapper>
    </Container>
  );
};
